import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BackIcon, SproutIcon } from '@/components/admin/greenCoffeeIcons';
import { GreenCoffeeQueueRow } from '@/components/admin/GreenCoffeeQueueRow';
import { GreenCoffeeReviewCard } from '@/components/admin/GreenCoffeeReviewCard';
import { isGradeLocked } from '@/components/admin/greenCoffeeStatus';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Layout } from '@/components/Layout';
import {
  type GradeGreenCoffeeResult,
  type GreenCoffeeFields,
  type GreenCoffeeListItem,
  type GreenCoffeeStatus,
  useCorrectGreenCoffee,
  useGradeGreenCoffee,
  useGreenCoffeeCounts,
  useGreenCoffees,
} from '@/hooks/useGreenCoffee';
import { useRoasters } from '@/hooks/useRoasters';

const TOAST_DURATION_MS = 2600;

const TABS: { id: GreenCoffeeStatus; label: string }[] = [
  { id: 'dlq', label: 'DLQ' },
  { id: 'pending', label: 'Pending' },
  { id: 'published', label: 'Published' },
  { id: 'bad', label: 'Bad' },
];

const EMPTY_COPY: Record<GreenCoffeeStatus, { title: string; subtitle: string }> = {
  dlq: {
    title: 'Nothing stuck',
    subtitle: 'Every staged coffee has a country. Ones without land here.',
  },
  pending: {
    title: 'Queue empty',
    subtitle: 'No green coffees waiting. New crawls will drop them here.',
  },
  published: {
    title: 'Nothing published yet',
    subtitle: 'Coffees the judge passes or you grade Good appear here.',
  },
  bad: { title: 'Nothing held', subtitle: 'Coffees you grade Bad appear here.' },
};

// Staff-only review of normalized green coffees (design: Green Coffee Review,
// judge gate revision). The judge publishes a PASS on its own; Pending holds
// its FAILs and the rows it has not judged, where Good publishes the coffee,
// Correct publishes it with the admin's field edits and Bad holds it with a
// note. A row the judge published is read-only here.
// Rendering is gated by the is_admin claim in App.tsx; the API's admin
// middleware is the real enforcement.
export function GreenCoffeeReviewPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<GreenCoffeeStatus>('pending');
  const { greenCoffees, isLoading, error } = useGreenCoffees(tab);
  const { counts, refetch: refetchCounts } = useGreenCoffeeCounts();
  const { roasters } = useRoasters();
  const { grade, isSaving: isGrading } = useGradeGreenCoffee();
  const { correct, isSaving: isCorrecting } = useCorrectGreenCoffee();
  const isSaving = isGrading || isCorrecting;

  // Local copy of the fetched bucket so a graded item leaves the queue
  // immediately and the overlay can advance to the next one.
  const [items, setItems] = useState<GreenCoffeeListItem[]>([]);
  useEffect(() => {
    setItems(greenCoffees ?? []);
  }, [greenCoffees]);

  const [reviewId, setReviewId] = useState<string | null>(null);
  useEffect(() => {
    setReviewId(null);
  }, [tab]);
  // Read after an awaited grade to tell whether the bucket changed under it.
  const tabRef = useRef(tab);
  tabRef.current = tab;

  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    },
    [],
  );
  const showToast = (message: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(message);
    toastTimerRef.current = setTimeout(() => setToast(null), TOAST_DURATION_MS);
  };

  const roasterNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const roaster of roasters ?? []) map.set(roaster.id, roaster.name);
    return map;
  }, [roasters]);
  const roasterName = (green: GreenCoffeeListItem) => roasterNames.get(green.roasterId) ?? null;

  const reviewIndex = items.findIndex((item) => item.id === reviewId);
  const reviewItem = reviewIndex === -1 ? null : items[reviewIndex];

  // Runs a grade or a correction, then takes the row out of the open bucket.
  async function settle(
    id: string,
    request: () => Promise<GradeGreenCoffeeResult>,
    confirm: (target: GreenCoffeeListItem | undefined) => string,
  ): Promise<void> {
    const gradedTab = tab;
    const target = items.find((item) => item.id === id);
    const result = await request();
    if (!result.ok) {
      if (result.message) showToast(result.message);
      return;
    }
    void refetchCounts();
    // A tab switch while the request was in flight replaced the bucket, so the
    // graded row is no longer part of what's on screen.
    if (tabRef.current !== gradedTab) return;

    const index = items.findIndex((item) => item.id === id);
    const remaining = items.filter((item) => item.id !== id);
    setItems((prev) => prev.filter((item) => item.id !== id));
    const confirmation = confirm(target);
    // Same index in the shortened list is the next item in the queue.
    const next = remaining[index];
    setReviewId(next ? next.id : null);
    // One toast: the cleared-queue note rides along with the confirmation
    // rather than replacing it in the same tick.
    showToast(remaining.length === 0 ? `${confirmation} — queue cleared. Nice work` : confirmation);
  }

  function submit(id: string, value: 'good' | 'bad', notes?: string): Promise<void> {
    return settle(
      id,
      () => grade(id, value, notes),
      (target) => {
        const name = target?.fields.name ?? 'coffee';
        return value === 'good'
          ? `Published${target?.isUpdate ? ' update' : ''}: ${name}`
          : `Marked bad: ${name}`;
      },
    );
  }

  function submitCorrection(id: string, fields: GreenCoffeeFields): Promise<void> {
    return settle(
      id,
      () => correct(id, fields),
      () => `Published corrected: ${fields.name}`,
    );
  }

  const pendingCount = counts?.pending ?? 0;

  return (
    <Layout>
      <div className="mx-auto max-w-[640px] px-4 pb-10" data-testid="green-coffee-page">
        <div className="flex items-center gap-2 pb-1 pt-5">
          <button
            type="button"
            onClick={() => void navigate('/me')}
            aria-label="Back to account"
            data-testid="green-coffee-back"
            className="-ml-1.5 flex h-8 w-8 items-center justify-center rounded-lg text-wolisho-brown opacity-50 transition-opacity hover:opacity-80"
          >
            <BackIcon size={18} />
          </button>
          <span className="text-xs font-bold uppercase tracking-wider text-wolisho-brown/60">
            Admin
          </span>
        </div>

        <h1 className="mt-1 text-2xl font-bold text-black">Green coffee review</h1>
        <p className="mb-4 mt-0.5 text-sm text-black/50" data-testid="green-coffee-subtitle">
          {pendingCount > 0 ? (
            <>
              <span className="font-bold text-black">{String(pendingCount)}</span> waiting · oldest
              first · the judge published the rest
            </>
          ) : (
            'All caught up · new crawls refill the queue'
          )}
        </p>

        {tab === 'pending' && items.length > 0 && (
          <button
            type="button"
            onClick={() => setReviewId(items[0]?.id ?? null)}
            data-testid="green-coffee-start"
            className="mb-3.5 inline-flex w-full items-center justify-center gap-2 rounded-lg border-0 bg-wolisho-red px-3 py-3 text-sm font-bold text-white"
          >
            <SproutIcon />
            Start reviewing · {String(items.length)}
          </button>
        )}

        <div className="mb-3.5 flex gap-1 rounded-lg bg-wolisho-brown/[0.05] p-1">
          {TABS.map((entry) => {
            const active = tab === entry.id;
            return (
              <button
                key={entry.id}
                type="button"
                aria-pressed={active}
                onClick={() => setTab(entry.id)}
                data-testid={`green-coffee-tab-${entry.id}`}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border-0 px-2 py-[7px] text-[12.5px] font-bold ${
                  active ? 'bg-white text-black' : 'bg-transparent text-wolisho-brown/50'
                }`}
              >
                {entry.label}
                <span
                  className={`text-[11px] font-bold ${active ? 'text-black/35' : 'text-wolisho-brown/35'}`}
                >
                  {String(counts?.[entry.id] ?? 0)}
                </span>
              </button>
            );
          })}
        </div>

        {/* Only with rows to act on — an empty DLQ has its own copy below. */}
        {tab === 'dlq' && items.length > 0 && (
          <p className="mb-3.5 text-[13px] text-black/50" data-testid="green-coffee-dlq-note">
            No country, so Good can&apos;t publish. Correct one to set its country, mark it bad, or
            fix the source and let the next crawl restage the listing.
          </p>
        )}

        {isLoading ? (
          <LoadingSpinner label="Loading green coffees" testId="green-coffee-loading" />
        ) : error ? (
          <p className="mt-4 text-sm text-black/50" data-testid="green-coffee-error">
            Couldn&apos;t load green coffees. Refresh to try again.
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl bg-wolisho-card">
            {items.length === 0 ? (
              <div className="px-4 py-12 text-center" data-testid="green-coffee-empty">
                <p className="mb-1 text-sm font-bold text-black/50">{EMPTY_COPY[tab].title}</p>
                <p className="text-[13px] text-black/35">{EMPTY_COPY[tab].subtitle}</p>
              </div>
            ) : (
              items.map((green, index) => (
                <div key={green.id}>
                  {index > 0 && <div className="mx-3.5 h-px bg-wolisho-brown/[0.06]" />}
                  <GreenCoffeeQueueRow
                    green={green}
                    roasterName={roasterName(green)}
                    onOpen={setReviewId}
                  />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {reviewItem && (
        <GreenCoffeeReviewCard
          green={reviewItem}
          roasterName={roasterName(reviewItem)}
          position={reviewIndex + 1}
          total={items.length}
          readOnly={isGradeLocked(reviewItem)}
          isSaving={isSaving}
          onGood={(id, notes) => void submit(id, 'good', notes)}
          onBad={(id, notes) => void submit(id, 'bad', notes)}
          onCorrect={(id, fields) => void submitCorrection(id, fields)}
          onClose={() => setReviewId(null)}
        />
      )}

      {toast && (
        <div
          className="pointer-events-none fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 whitespace-nowrap rounded-lg bg-wolisho-brown px-5 py-2.5 text-[13px] font-bold text-white"
          role="status"
          data-testid="green-coffee-toast"
        >
          {toast}
        </div>
      )}
    </Layout>
  );
}
